import { Stack } from '@mui/material';
import { isEmpty } from 'lodash';
import { useCallback } from 'react';

import { FormModal } from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions';
import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';
import { useDocumentTypesValidation } from '@/modules/documents/MultiDocumentUploader/hooks/useDocumentTypesValidation';

import { MultiDocumentUploaderWithListV2 } from '../MultiDocumentUploader/MultiDocumentUploaderWithListV2';

export interface DocumentUploaderModalProps {
  onClose: () => void;
  onSubmit: () => Promise<void>;
  isOpen: boolean;
  householdId: string;
  loading?: boolean;
  additionalDetails?: React.ReactNode;
  /** Override the modal z-index (e.g. to render above a SideDrawer). */
  zIndex?: number;
}

/**
 * DocumentUploaderModal is a modal that allows the user to upload documents to a household. It
 * expects MultiDocumentUploaderProvider to be available, and expects the consumer to handle the
 * persistence of the uploaded files.
 */
export function DocumentUploaderModal({
  onClose,
  onSubmit,
  isOpen,
  householdId,
  loading,
  additionalDetails,
  zIndex,
}: DocumentUploaderModalProps) {
  const { uploadedFiles } = useMultiDocumentUploaderContext();
  const allDocumentTypesSelected = useDocumentTypesValidation();

  const handleSubmit = useCallback(() => {
    if (isEmpty(uploadedFiles)) {
      onClose();
      return;
    }
    return onSubmit();
  }, [onClose, onSubmit, uploadedFiles]);

  const actions = (
    <FormModalActions.Provider disabled={loading}>
      <FormModalActions.CancelButton onClick={onClose} />
      <FormModalActions.SaveButton
        onClick={handleSubmit}
        disabled={!allDocumentTypesSelected || loading}
      >
        Upload documents
      </FormModalActions.SaveButton>
    </FormModalActions.Provider>
  );

  return (
    <FormModal
      onClose={onClose}
      isOpen={isOpen}
      heading="Upload documents"
      actions={actions}
      zIndex={zIndex}
    >
      {additionalDetails && <Stack mb={2}>{additionalDetails}</Stack>}
      <MultiDocumentUploaderWithListV2
        variant="expanded"
        householdId={householdId}
      />
    </FormModal>
  );
}
